// Trip and Vertical Constants for FusionCab - Backend Compatible

export const VERTICALS = {
  INTERCITY: "intercity",
  AIRPORT: "airport",
  HOURLY: "hourly",
} as const;

export const TRIP_TYPES = {
  ONEWAY: "one-way",
  ROUNDTRIP: "round-trip",
  MULTICITY: "multi-city",
  PICKUP: "pickup",
  DROPOFF: "drop-off",
  HRS4: "4hrs/40kms",
  HRS8: "8hrs/80kms",
  HRS12: "12hrs/120kms",
} as const;

// Duration mapping for hourly rentals
export const HOURLY_DURATION_MAPPING = {
  "4": "4hrs/40kms",
  "8": "8hrs/80kms",
  "12": "12hrs/120kms",
} as const;

// Type definitions for TypeScript
export type VerticalType = (typeof VERTICALS)[keyof typeof VERTICALS];
export type TripType = (typeof TRIP_TYPES)[keyof typeof TRIP_TYPES];

// Display options for the vertical tabs (Intercity / Airport / Hourly)
export const VERTICAL_OPTIONS: { value: VerticalType; label: string }[] = [
  { value: VERTICALS.INTERCITY, label: "Intercity" },
  { value: VERTICALS.AIRPORT, label: "Airport" },
  { value: VERTICALS.HOURLY, label: "Hourly rentals" },
];

// Trip-type sub-options, scoped per vertical, for data-driven tab rendering
export const TRIP_TYPES_BY_VERTICAL: Record<
  VerticalType,
  { value: TripType; label: string }[]
> = {
  [VERTICALS.INTERCITY]: [
    { value: TRIP_TYPES.ONEWAY, label: "One-Way" },
    { value: TRIP_TYPES.ROUNDTRIP, label: "Round Trip" },
    { value: TRIP_TYPES.MULTICITY, label: "Multi City" },
  ],
  [VERTICALS.AIRPORT]: [
    { value: TRIP_TYPES.PICKUP, label: "Pickup" },
    { value: TRIP_TYPES.DROPOFF, label: "Drop Off" },
  ],
  [VERTICALS.HOURLY]: [
    { value: TRIP_TYPES.HRS4, label: "4hrs/40kms" },
    { value: TRIP_TYPES.HRS8, label: "8hrs/80kms" },
    { value: TRIP_TYPES.HRS12, label: "12hrs/120kms" },
  ],
};

// Default trip type to select when switching to a given vertical
export const DEFAULT_TRIP_TYPE_BY_VERTICAL: Record<VerticalType, TripType> = {
  [VERTICALS.INTERCITY]: TRIP_TYPES.ONEWAY,
  [VERTICALS.AIRPORT]: TRIP_TYPES.PICKUP,
  [VERTICALS.HOURLY]: TRIP_TYPES.HRS4,
};

// Reverse lookup of HOURLY_DURATION_MAPPING - derives the "hours" search
// parameter directly from an already-selected hourly TripType value, since
// the hourly duration is chosen via the trip-type tabs, not a separate field.
export function getHourlyDurationKey(tripType: TripType): string | undefined {
  const entry = Object.entries(HOURLY_DURATION_MAPPING).find(
    ([, value]) => value === tripType
  );
  return entry?.[0];
}

// Shared field-visibility/validation rules for every search form (Landing,
// Dashboard, and their sub-components) so they can never drift apart again.
//
// For Airport, the backend always stores one side of the Plan as a dedicated
// "<City> Airport" location (see fusion_backend seed data): for Pickup the
// airport is the source (you're picked up at the airport), for Drop-off the
// airport is the destination (you're dropped off at the airport). So the
// airport-side field is hidden here and resolved to the matching airport
// City record before the search request is sent.
export function isSourceFieldVisible(
  vertical: VerticalType,
  tripType: TripType
): boolean {
  return !(vertical === VERTICALS.AIRPORT && tripType === TRIP_TYPES.PICKUP);
}

export function isDestinationFieldVisible(
  vertical: VerticalType,
  tripType: TripType
): boolean {
  return (
    vertical !== VERTICALS.HOURLY &&
    !(vertical === VERTICALS.AIRPORT && tripType === TRIP_TYPES.DROPOFF)
  );
}

export function isReturnLegRequired(tripType: TripType): boolean {
  return tripType === TRIP_TYPES.ROUNDTRIP;
}
